import React, { useRef, useState } from 'react'
import { Mic, ArrowUp, Square } from 'lucide-react'
import { Button } from 'components/ui'
import {
  ChatComposer,
  ChatComposerEditor,
  ChatComposerActions,
  ChatComposerActionGroup,
  ChatComposerHint,
} from 'components/ui/ChatComposer'
import { useChatDictation } from 'components/ui/ChatComposer/useChatDictation'
import { ChatDictation } from 'components/ui/ChatComposer/Dictation'
import chatTheme from 'components/ui/ChatSurface/styles.module.css'
import { usePromptGenerator } from '../../PromptGeneratorContext'
import styles from './styles.module.css'

/** Text-only adapter. The feature owns draft and WebSocket send semantics. */
export const PromptGeneratorInput: React.FC = () => {
  const [text, setText] = useState('')
  const [expanded, setExpanded] = useState(false)

  const {
    sendMessage,
    isBotResponseInProgress,
    isSessionExpired,
    stopVoicePlayback,
    isWebSocketReady,
    hasWebSocketError,
  } = usePromptGenerator()
  const input = useRef<HTMLTextAreaElement>(null)
  const sendLock = useRef(false)
  const disabled = isBotResponseInProgress || isSessionExpired || !isWebSocketReady || hasWebSocketError
  const dictation = useChatDictation({ text, onChange: setText, active: !disabled, input })
  const { speech } = dictation
  const canSend = !disabled && !speech.listening && !!text.trim()

  const send = () => {
    if (!canSend || sendLock.current) return
    sendLock.current = true
    try {
      sendMessage(text.trim())
      setText('')
      setExpanded(false)
    } finally {
      // Block two synchronous submissions before React commits the cleared draft.
      Promise.resolve().then(() => {
        sendLock.current = false
      })
    }
  }

  const blockFiles = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <div
      className={[chatTheme.surface, styles.input].join(' ')}
      onDragOver={blockFiles}
      onDrop={blockFiles}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && speech.listening) {
          event.preventDefault()
          event.stopPropagation()
          dictation.cancel()
        }
      }}
    >
      <ChatComposer
        onClick={(event) => {
          if (
            !disabled &&
            !speech.listening &&
            event.target instanceof Element &&
            !event.target.closest('button, a, input, textarea, select, [role="button"]')
          )
            input.current?.focus({ preventScroll: true })
        }}
      >
        <ChatDictation {...dictation} />
        <ChatComposerEditor
          id="prompt-generator-input"
          label="Message prompt generator"
          placeholder="Message…"
          input={input}
          value={text}
          expanded={expanded}
          hidden={speech.listening}
          disabled={disabled}
          onChange={(value) => {
            speech.handleManualInput()
            setText(value)
          }}
          onSend={send}
          onCancel={dictation.cancel}
          onToggleExpanded={() => setExpanded((value) => !value)}
        />
        <ChatComposerActions>
          <span />
          <ChatComposerActionGroup>
            {speech.listening ? (
              <>
                <Button variant="secondary" onClick={dictation.cancel}>
                  Cancel
                </Button>
                <Button
                  bg="#1E1E1E"
                  startIcon={<Square size={12} fill="currentColor" />}
                  onClick={speech.stopListening}
                >
                  Stop and insert
                </Button>
              </>
            ) : (
              <>
                {speech.speechSupported && (
                  <Button
                    variant="icon"
                    aria-label="Start voice input"
                    title="Start voice input"
                    isDisabled={disabled || speech.isMicrophoneAvailable === false}
                    onClick={() => {
                      stopVoicePlayback()
                      dictation.start()
                    }}
                  >
                    <Mic size={18} />
                  </Button>
                )}
                <Button
                  aria-label="Send message"
                  bg="#1E1E1E"
                  borderRadius="50%"
                  minW="34px"
                  w="34px"
                  h="34px"
                  p={0}
                  isDisabled={!canSend}
                  onClick={send}
                >
                  <ArrowUp size={18} />
                </Button>
              </>
            )}
          </ChatComposerActionGroup>
        </ChatComposerActions>
      </ChatComposer>
      {speech.isMicrophoneAvailable === false && (
        <p role="alert">Microphone permission denied. Allow microphone access in your browser’s site settings.</p>
      )}
      <ChatComposerHint>
        {speech.listening
          ? 'Dictation fills the field — it never sends on its own'
          : 'Enter to send · Shift+Enter for a new line'}
      </ChatComposerHint>
    </div>
  )
}
